import { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Box, Skeleton, Typography, styled } from "@mui/material";
import {
  DocumentProps,
  PrescriptionDocumentType,
  PrescriptionStatus,
  PrescriptionType,
  PrescriptionPayload,
  RequestType,
  PrescriptionTypeMapper,
} from "@interfaces";
import { PDFViewer, PDFViewerProps, SecondaryActionButton } from "@components";
import { PrescriptionDetailCard } from "../../PrescriptionForm/components/PrescriptionDetailCard";
import { useGetPDFs } from "@hooks";
import { generatePdfName } from "@utils";

import { PrescriptionStatusLabel } from "@components/PrescriptionStatusLabel";

const SummaryContainer = styled(Box)({
  display: "flex",
  flexDirection: "column",
  gap: "16px",
});

const Row = styled(Box)({
  display: "flex",
  flexDirection: "row",
  alignItems: "center",
  justifyContent: "space-between",
});

const SectionTitle = styled(Typography)({
  display: "flex",
  fontWeight: 600,
  gap: "8px",
});

const PrescriptionDetailsContainer = styled(Box)({
  display: "flex",
  flexDirection: "column",
  gap: "8px",
  margin: "8px 0",
});

const PrescriptionListContainer = styled(Box)({
  display: "flex",
  flexDirection: "column",
  gap: "24px",
});

const ButtonContainer = styled(Box)({
  display: "flex",
  justifyContent: "center",
  gap: "8px",
});

const ShowPrescriptionButton = styled(SecondaryActionButton)({
  padding: "4px",
});

const SkeletonContainer = styled(Box)({
  display: "flex",
  flexDirection: "column",
  marginTop: "24px",
  gap: "24px",
});

interface ReducedPrescriptionData {
  prescriptionId: string;
  requestId: string;
  pdfUrl: string;
  prescriptionType: PrescriptionType;
  prescriptionStatus: PrescriptionStatus;
  holdingDate?: number;
}

export interface TreatmentAction {
  onShow: () => void;
  disabled: boolean;
}

interface Props {
  data: PrescriptionPayload;
  isFetching: boolean;
  onTreatmentActionChange?: (action: TreatmentAction | null) => void;
}

export const HistoricPrescription = ({
  data,
  isFetching,
  onTreatmentActionChange,
}: Props) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { id: requestId, patient, documents: prescriptions } = data;
  const [documentData, setDocumentData] = useState<DocumentProps>(
    {} as DocumentProps,
  );

  const {
    data: pdfData,
    isPending,
    isSuccess,
    isError,
  } = useGetPDFs({
    requestId,
  });

  const reducedPrescriptionData: Record<string, ReducedPrescriptionData> =
    useMemo(
      () =>
        prescriptions.reduce(
          (
            reducedPrescription,
            { id, type: prescriptionType, prescriptionStatus, holdingDate },
          ) => {
            const treatmentPdfUrl = pdfData?.treatment?.url;

            if (treatmentPdfUrl) {
              reducedPrescription[requestId] = {
                pdfUrl: treatmentPdfUrl,
                prescriptionType,
                prescriptionStatus,
                holdingDate,
                prescriptionId: id,
                requestId,
              };
            } else {
              const pdfUrl = pdfData?.documents
                ?.map(({ url }) => url)
                .find((url) => url.includes(id));

              if (pdfUrl) {
                reducedPrescription[id] = {
                  pdfUrl,
                  prescriptionType,
                  prescriptionStatus,
                  holdingDate,
                  prescriptionId: id,
                  requestId,
                };
              }
            }
            return reducedPrescription;
          },
          {} as Record<string, ReducedPrescriptionData>,
        ),
      [prescriptions, pdfData, requestId],
    );

  const onClose = () => setDocumentData({} as DocumentProps);

  const showTreatmentRef = useRef<() => void>(() => {});
  showTreatmentRef.current = () => {
    setDocumentData({
      ...documentData,
      documentId: requestId,
      documentName: generatePdfName({
        documentType: RequestType.PRESCRIPTION,
        patient,
      }),
    });
  };

  const treatmentAvailable = Boolean(pdfData?.treatment?.url);
  const treatmentDisabled =
    isError || !reducedPrescriptionData[requestId]?.pdfUrl;

  useEffect(() => {
    if (!onTreatmentActionChange) return;
    if (treatmentAvailable) {
      onTreatmentActionChange({
        onShow: () => showTreatmentRef.current(),
        disabled: treatmentDisabled,
      });
    } else {
      onTreatmentActionChange(null);
    }
  }, [treatmentAvailable, treatmentDisabled, onTreatmentActionChange]);

  if (Object.keys(documentData).length !== 0) {
    const prevRoute = location.pathname;
    if (isPending) {
      return <PDFViewer.Skeleton prevRoute={prevRoute} />;
    } // TODO: If isError, maybe add a modal with an error message
    else if (isError) {
      onClose();
    } else if (isSuccess) {
      const selectedPrescription =
        reducedPrescriptionData[documentData.documentId];
      const selectedPrescriptionTitle =
        selectedPrescription.prescriptionType === "SIMPLE"
          ? "Receta simple"
          : "Receta retenida";
      const navigationState: PDFViewerProps = {
        documentProps: {
          ...documentData,
          documentUrl: selectedPrescription.pdfUrl,
        },
        pdfRenderingProps: {
          title: selectedPrescriptionTitle,
          prevRoute,
        },
        prescriptionData: {
          requestId: selectedPrescription.requestId,
          prescriptionId: selectedPrescription.prescriptionId,
          prescriptionStatus: selectedPrescription.prescriptionStatus,
          holdingDate: selectedPrescription.holdingDate,
          type: selectedPrescription.prescriptionType ?? undefined,
          isFetching,
          hasMoreThanOnePrescription: Boolean(
            prescriptions.length > 1 && pdfData?.treatment?.url,
          ),
        },
      };
      navigate("/app/solicitudes/visualizacion-documento", {
        state: navigationState,
      });
    }
  }

  return (
    <SummaryContainer>
      <PrescriptionListContainer>
        {prescriptions.map(({ id, details, type, prescriptionStatus }) => {
          const title =
            type === PrescriptionDocumentType.SIMPLE
              ? "Receta simple"
              : "Receta retenida";
          return (
            <Box key={`${title}-${id}`}>
              <Row>
                <SectionTitle>{title}</SectionTitle>
                <Box display="flex" alignItems="center" gap="8px">
                  {prescriptionStatus !== PrescriptionStatus.ISSUED && (
                    <PrescriptionStatusLabel status={prescriptionStatus} />
                  )}
                  {pdfData && !pdfData.treatment && (
                    <ButtonContainer>
                      <ShowPrescriptionButton
                        onClick={() =>
                          setDocumentData({
                            ...documentData,
                            documentId: id,
                            documentName: generatePdfName({
                              documentType: RequestType.PRESCRIPTION,
                              patient,
                              documentNameDetails: PrescriptionTypeMapper[type],
                            }),
                          })
                        }
                        disabled={
                          isError || !reducedPrescriptionData[id]?.pdfUrl
                        }
                      >
                        Ver receta
                      </ShowPrescriptionButton>
                    </ButtonContainer>
                  )}
                </Box>
              </Row>
              <PrescriptionDetailsContainer>
                {details.map((detail) => {
                  return (
                    <PrescriptionDetailCard key={detail.id} detail={detail} />
                  );
                })}
              </PrescriptionDetailsContainer>
            </Box>
          );
        })}
      </PrescriptionListContainer>
    </SummaryContainer>
  );
};

HistoricPrescription.Skeleton = function HistoricPrescriptionSkeleton() {
  return (
    <SkeletonContainer>
      {Array.from({ length: 2 }).map((_, i) => (
        <div key={i}>
          <Row>
            <Skeleton variant="rectangular" height={20} width={160} />
          </Row>
          <PrescriptionDetailsContainer>
            <PrescriptionDetailCard.Skeleton />
          </PrescriptionDetailsContainer>
        </div>
      ))}
    </SkeletonContainer>
  );
};
